import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Accordion } from '@appcues/sonar';
import { FieldSet } from 'ext/components/ui';
import BlockContentShape from 'entities/block/shape';
import { selectUserPreferences } from 'entities/user-preferences/reducer';
import { COLOR_MODES } from 'lib/user-preferences/user-preferences';
import { HERO, BLOCK_LABELS } from 'lib/block/block';
import ImageUploader from 'components/ImageUploader';
import { heroBackgroundTemplate } from 'components/Editor/Templates/Content/hero';
import {
  Content,
  Controls,
  ThemeVariantSelect,
} from 'components/SideBarSettings/Shared';
import ContentActions from 'components/SideBarSettings/Content/ContentActions';
import Style from './Style';
import AlignmentSpacing from './AlignmentSpacing';

export function HeroSettings({
  content,
  contentWithVariations,
  onChange,
  colorMode,
}) {
  const { id, style } = content ?? {};
  const { imageUrl } = style?.backgroundImage ?? {};

  const handleContentChange = updatedContent => {
    onChange({
      blockId: id,
      contentChunk: updatedContent,
      useOriginalId: true,
    });
  };

  const handleBackgroundImage = useCallback(
    params => {
      const { url, intrinsicSize } = params ?? {};

      if (!params) {
        const { backgroundImage, ...styleRest } = style;
        onChange({
          blockId: id,
          contentChunk: {
            style: styleRest,
          },
          useOriginalId: true,
        });
        return;
      }

      onChange({
        blockId: id,
        contentChunk: {
          style: {
            ...style,
            backgroundImage: {
              ...(style?.backgroundImage ??
                heroBackgroundTemplate.backgroundImage),
              imageUrl: url,
              blurHash: '',
              intrinsicSize,
            },
          },
        },
        useOriginalId: true,
      });
    },
    [id, style, onChange]
  );

  return (
    <Content>
      <ThemeVariantSelect
        type={HERO}
        content={content}
        contentWithVariations={contentWithVariations}
        onChange={onChange}
      />
      <Controls>
        <FieldSet>
          <ImageUploader
            currentImage={imageUrl}
            onUpload={handleBackgroundImage}
          />
        </FieldSet>
      </Controls>

      <Accordion.Root
        collapsible="true"
        defaultValue={['style', 'alignment-spacing']}
        type="multiple"
      >
        <Style content={content} onChange={onChange} colorMode={colorMode} />

        <AlignmentSpacing
          style={style}
          spacingLabel={BLOCK_LABELS[HERO]}
          onChange={handleContentChange}
        />
        <ContentActions blockId={id} context={HERO} />
      </Accordion.Root>
    </Content>
  );
}

HeroSettings.propTypes = {
  content: BlockContentShape,
  contentWithVariations: BlockContentShape,
  onChange: PropTypes.func,
  colorMode: PropTypes.oneOf(COLOR_MODES),
};

const mapStateToProps = state => ({
  colorMode: selectUserPreferences(state).colorMode,
});

export default connect(mapStateToProps)(HeroSettings);
